import React, { useEffect } from 'react';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useForm, FormProvider } from 'react-hook-form';
import { useTheme } from '@mui/material/styles';
import { useFlag } from '@openfeature/react-sdk';

import NRDialog from '../../components/NRDialog/NRDialog';
import NRControlledTextField from '../../components/NRControlledTextField/NRControlledTextField';
import NRControlledSelect from '../../components/NRControlledSelect/NRControlledSelect';
import LicenseDropdown from '../../components/SBOM/LicenseList/LicenseListDropdown';
import NRAssetCpeChipInput, { flushPendingAssetCpe } from '../../components/Asset/NRAssetCpeChipInput/NRAssetCpeChipInput';
import NRButtonProgress from '../../components/NRButtonProgress/NRButtonProgress';
import useNRMutation from '../../graphql/UseNRMutation';
import { QL_UPDATE_ASSET } from '../../graphql/queries/asset';
import { CONSTANTS, FEATURE_FLAGS } from '../../constants/constants';
import { capitalizeFirstLetter, isEmpty } from '../../utils/utils';
import { SBOM_TYPES } from '../../components/SBOM/SBOM.constants';

const { NAME_LABEL, MANUFACTURER_LABEL, MODEL_LABEL, VERSION_LABEL } = CONSTANTS.TRANSACTION_ASSET;
const { CLOSE } = CONSTANTS.ACTIONS;
const { FIRMWARE, BINARY, SBOM } = CONSTANTS.TABLE.ASSETS.TYPES;

const TYPE_OPTIONS = [
  { value: FIRMWARE, label: capitalizeFirstLetter(BINARY) },
  { value: SBOM, label: SBOM }
];

const { CLASSIFICATION_SELECT_OPTIONS } = CONSTANTS.TABLE.ASSETS;

/** Assets uploaded before classification existed read back as UNSPECIFIED, which is not selectable. */
function toFormClassification(classification) {
  return CLASSIFICATION_SELECT_OPTIONS.some(option => option.value === classification) ? classification : '';
}

function toFormType(assetType) {
  if (!assetType) return '';
  if (assetType === FIRMWARE) return FIRMWARE;
  if (SBOM_TYPES.includes(assetType)) return SBOM;
  return assetType;
}

function getDefaultValues(asset) {
  if (!asset) {
    return {
      name: '',
      vendor: '',
      product: '',
      version: '',
      type: '',
      classification: '',
      assetCpes: [],
      licenses: null,
      cpeInput: '',
      cpeInputError: ''
    };
  }

  return {
    name: asset.name || '',
    vendor: asset.vendor || '',
    product: asset.product || '',
    version: asset.version || '',
    type: toFormType(asset.type),
    classification: toFormClassification(asset.classification),
    assetCpes: asset.assetCpes?.length ? [...asset.assetCpes] : asset.assetCpe ? [asset.assetCpe] : [],
    licenses: asset.license ? [asset.license] : null,
    cpeInput: '',
    cpeInputError: ''
  };
}

function NRAssetEditModal({ open, onClose, asset, onAssetUpdated, title = 'Edit Asset Details' }) {
  const theme = useTheme();
  const { value: isLicenseIssueEnable } = useFlag(FEATURE_FLAGS.LICENSE_ISSUES_FLAG, false);
  const methods = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: getDefaultValues(asset)
  });
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors }
  } = methods;
  const licenses = watch('licenses');
  const cpeInputError = watch('cpeInputError');

  const [updateAsset, , loading] = useNRMutation(
    QL_UPDATE_ASSET,
    'asset',
    'Asset updated successfully.',
    'Asset was unable to save, please try again.'
  );

  useEffect(() => {
    if (open) {
      reset(getDefaultValues(asset));
    }
  }, [open, asset, reset]);

  const handleClose = () => {
    onClose?.();
  };

  const onSubmit = data => {
    if (!asset?.id) return;
    if (!flushPendingAssetCpe({ getValues, setValue })) return;

    const assetCpes = Array.from(new Set(getValues('assetCpes') || []));
    const licenseValue = isLicenseIssueEnable ? data.licenses?.[0] ?? '' : undefined;

    updateAsset({
      variables: {
        args: {
          id: asset.id,
          name: data.name,
          vendor: data.vendor,
          product: data.product,
          version: data.version,
          assetCpes,
          classification: data.classification,
          ...(isLicenseIssueEnable && { license: licenseValue })
        }
      },
      onCompleted: response => {
        const updated = response?.asset?.update;
        if (updated) {
          onAssetUpdated?.(updated);
        }
        handleClose();
      }
    });
  };

  return (
    <FormProvider {...methods}>
      <NRDialog isOpen={!!open} setIsOpen={handleClose} maxWidth="sm" fullWidth ariaLabelledBy="asset-edit-dialog-title">
        <DialogTitle id="asset-edit-dialog-title">
          <Grid item xs={12} sx={{ margin: 'unset', display: 'flex', justifyContent: 'space-between' }}>
            <Typography tabIndex={0} aria-label={title} component="span" variant="h3">
              {title}
            </Typography>
            <Tooltip title={CLOSE} placement="right">
              <IconButton aria-label="Close" onClick={handleClose}>
                <CloseOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        </DialogTitle>
        <DialogContent sx={{ paddingBottom: theme.spacing(0.5) }}>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
            <NRControlledTextField
              name="name"
              label={NAME_LABEL}
              control={control}
              rules={{ required: CONSTANTS.VALIDATIONS.REQUIRED }}
              error={!!errors.name}
              helperText={errors.name?.message}
              required
              defaultValue=""
            />
            <NRControlledTextField name="product" label={MODEL_LABEL} control={control} defaultValue="" />
            <Box display="flex" gap={2} width="100%">
              <Box flex={1}>
                <NRControlledTextField name="vendor" label={MANUFACTURER_LABEL} control={control} defaultValue="" />
              </Box>
              <Box flex={1}>
                <NRControlledTextField name="version" label={VERSION_LABEL} control={control} defaultValue="" />
              </Box>
            </Box>
            <Box display="flex" gap={2} width="100%">
              <Box flex={1}>
                <NRControlledSelect
                  size="small"
                  id="asset-type"
                  name="type"
                  label="Format"
                  control={control}
                  options={TYPE_OPTIONS}
                  disabled
                  displayEmpty={false}
                  showEmptyValue={false}
                  showIcon={false}
                  sxProps={{ width: '100%' }}
                />
              </Box>
              <Box flex={1}>
                <NRControlledSelect
                  size="small"
                  id="asset-classification"
                  name="classification"
                  label="Type"
                  control={control}
                  options={CLASSIFICATION_SELECT_OPTIONS}
                  rules={{ required: CONSTANTS.VALIDATIONS.REQUIRED }}
                  error={!!errors.classification}
                  helperText={errors.classification?.message}
                  displayEmpty={false}
                  showEmptyValue={false}
                  showIcon={false}
                  sxProps={{ width: '100%' }}
                />
              </Box>
            </Box>
            {isLicenseIssueEnable && (
              <LicenseDropdown value={licenses} setValue={newLicenses => setValue('licenses', !!newLicenses ? [newLicenses] : null)} />
            )}
            <NRAssetCpeChipInput />
          </Box>
        </DialogContent>
        <DialogActions>
          <Grid container>
            <Grid item xs={12} sx={{ p: theme.spacing(2, 3), display: 'flex', justifyContent: 'end', alignItems: 'end' }}>
              <Button variant="outlined" color="secondary" onClick={handleClose} sx={{ mr: 2 }}>
                Cancel
              </Button>
              <NRButtonProgress
                variant="contained"
                color="secondary"
                disabled={!isEmpty(errors) || !!cpeInputError || loading}
                isLoading={loading}
                type="button"
                label="Save"
                onClick={handleSubmit(onSubmit)}
              />
            </Grid>
          </Grid>
        </DialogActions>
      </NRDialog>
    </FormProvider>
  );
}

export default NRAssetEditModal;
